import { IAny, IAnyObject, IObjKey } from './type';

/**
 * 判断响应是否成功。
 *
 * @param {Partial<IHTTPResponse>} [Response] - HTTP响应的部分对象。
 * @returns {boolean} 如果响应码为成功码，则返回true，否则返回false。
 */
export const isSuccess = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.success;

/**
 * 判断是否为错误请求 (Bad Request)。
 *
 * @param {Partial<IHTTPResponse>} [Response] - HTTP 响应对象的部分属性。
 * @returns {boolean} 如果响应码为 `badRequest`，则返回 `true`，否则返回 `false`。
 */
export const isBadRequest = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.badRequest;

/**
 * 检查响应是否为未授权状态。
 *
 * @param {Partial<IHTTPResponse>} [Response] - 部分 HTTP 响应对象。
 * @returns {boolean} 如果响应代码为未授权，则返回 true。
 */
export const isUnauthorized = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.unauthorized;

/**
 * 检查响应是否为禁止访问状态。
 *
 * @param {Partial<IHTTPResponse>} [Response] - 部分 HTTP 响应对象。
 * @returns {boolean} 如果响应代码为禁止访问状态，则返回 true。
 */
export const isForbidden = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.forbidden;

/**
 * 判断响应是否为禁止访问状态
 *
 * @param {Partial<IHTTPResponse>} [Response] - HTTP响应的部分对象
 * @returns {boolean} 如果响应代码为禁止访问状态，则返回true
 */
export const isNetworkError = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.networkError;

/**
 * 判断响应是否为捕获 try catch 的错误
 *
 * @param {Partial<IHTTPResponse>} [Response] - HTTP响应的部分对象
 * @returns {boolean} 如果响应代码为捕获 try catch 的错误，则返回true
 */
export const isTryCatchError = (Response?: Partial<IHTTPResponse>) => Response?.code === IHTTPCode.tryCatchError;

/**
 * 根据状态码返回对应的 HTTP 代码。
 *
 * @param {number} status - HTTP 状态码。
 * @returns {number} 如果状态码在 200 到 299 之间，返回 IHTTPCode.success，否则返回原始状态码。
 */
export const getCodeByStatus = (status: number) => ((status >= 200 && status < 300) ? IHTTPCode.success : status);

/**
 * 获取 try catch 错误的 Response
 *
 * @param {Error} error - 错误对象
 * @returns {Partial<IHTTPResponse>} 返回 try catch 错误的 Response
 */
export const getTryCatchErrorResponse = (error?: unknown) => {
  const msg = error instanceof Error ? error.message : 'try catch error';
  const res: Partial<IHTTPResponse> = { code: IHTTPCode.tryCatchError, msg, error };
  if (error) {
    if (typeof error === 'object' && error !== null && 'data' in error) {
      res.data = error.data;
    }
  }
  return res;
};

/**
 * 单 key 包装自动解包（gRPC / pb 适配层，跨端通用）。
 *
 * @remarks
 * gRPC / pb 规范不允许 RPC method 返回基础类型或数组，必须是 message（object）——
 * 后端把 `boolean` / `T[]` 这类响应包成 `{ value: T }` 或 `{ data: T[] }` 单 key 对象。
 * 本函数把这层包装拆掉，让前端 `response.data` 直接是裸类型 `T` / `T[]`，
 * 便于 Schema / Store 直接消费。
 *
 * 适用：Web (axios) / Expo fetch / Taro.request 等所有 apiExecutor 实现的响应归一化。
 *
 * 解包规则：
 * - 只解 `{ value: T }` 与 `{ data: T[] }` 两种单 key 包装
 * - 多 key 对象、数组、基础类型、null / undefined 一律原样返回
 *
 * @param data - 业务 `data` 字段（已经过 `{ code, msg, data }` envelope 归一化）
 * @returns 解包后的 data；不命中规则则原样返回
 *
 * @example
 * ```ts
 * unwrapSingleKeyData({ value: true });          // → true
 * unwrapSingleKeyData({ data: [1, 2, 3] });      // → [1, 2, 3]
 * unwrapSingleKeyData({ id: 1 });                // → { id: 1 }（多 key 不拆）
 * unwrapSingleKeyData({ value: true, msg: 'x' });// → 原样（不只 1 个 key）
 * unwrapSingleKeyData({ foo: 1 });               // → 原样（单 key 但非 value/data）
 * unwrapSingleKeyData([1, 2]);                   // → 原样
 * unwrapSingleKeyData(null);                     // → null
 * ```
 */
export function unwrapSingleKeyData<T = IAny>(data: T): T {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return data;
  }
  const objData = data as IAnyObject;
  const keys = Object.keys(objData);
  if (keys.length !== 1) {
    return data;
  }
  const [key] = keys;
  // pb / gRPC 约定：基础类型 / 单对象包 value，数组 / 列表包 data
  if (key === 'value' || key === 'data') {
    return objData[key] as T;
  }
  return data;
}

/**
 * HTTP响应接口
 *
 * @template R 响应数据类型，默认为 `any`
 * @template P 请求配置参数类型，默认为 `any`
 *
 * @property {IHTTPCode | number} code 响应状态码，可以是自定义的 `IHTTPCode` 或数字
 * @property {string} msg 响应信息
 * @property {R} data 响应数据
 * @property {IAPIRequestConfig<P>} [config] 请求配置，可选
 * @property {number} [status] HTTP状态码，可选
 * @property {string} [statusText] HTTP状态文本，可选
 * @property {Record<ObjKey, any>} [headers] 响应头信息，可选
 * @property {any} [key: string] 其他任意附加属性
 */
export interface IHTTPResponse<R = IAny, P = IAny> {
  code: IHTTPCode | number
  msg: string,
  data: R
  config?: IAPIRequestConfig<P>
  status?: number;
  statusText?: string;
  headers?: Record<IObjKey, IAny>;
  [key: string]: IAny
}

/**
 * API 请求的配置对象。
 *
 * @template V - 请求参数和数据的类型。
 *
 * @property {string} [url] - API 端点的 URL。
 * @property {IMethod | string} [method] - 请求使用的 HTTP 方法。
 * @property {V | any} [params] - 要随请求发送的 URL 参数。
 * @property {V} [data] - 要随请求发送的数据。
 * @property {any} [key: string] - 任何其他附加属性。
 */
export type IAPIRequestConfig<V = IAny> = {
  url?: string,
  method?: IMethod | string,
  params?: V | IAny,
  data?: V,
  [key: string]: IAny
};


/**
 * 表示分页数据的接口。
 *
 * @template T - 页中数据项的类型。默认为键为 `ObjKey` 类型，值为任意类型的记录。
 *
 * @property {number} page - 当前页码。
 * @property {number} pageSize - 每页的项目数。
 * @property {number} total - 项目总数。
 * @property {number} totalPages - 总页数。
 * @property {T[]} data - 当前页的数据项数组。
 * @property {any} [key: string] - 可以动态添加的其他属性。
 */
export interface IPageData<T extends object = Record<IObjKey, IAny>> {
  page: number,
  pageSize: number,
  total: number,
  totalPages: number,
  data: T[],
  [key: string]: IAny
}

/**
 * HTTP状态码枚举。
 *
 * @enum {number}
 * @readonly
 * @property {number} success - 请求成功，状态码为0。
 * @property {number} badRequest - 错误请求，状态码为400。
 * @property {number} unauthorized - 未授权，状态码为401。
 * @property {number} forbidden - 禁止访问，状态码为403。
 * @property {number} networkError - 网络错误，状态码为600。
 */
export enum IHTTPCode {
  success = 0,
  badRequest = 400,
  unauthorized = 401,
  forbidden = 403,
  networkError = 600,
  // 捕获 try catch 的错误
  tryCatchError = 999,
}

/**
 * HTTP 请求方法类型。
 *
 * 该类型定义了常见的 HTTP 请求方法，包括：
 * - 'GET': 获取资源
 * - 'DELETE': 删除资源
 * - 'HEAD': 获取资源的头部信息
 * - 'OPTIONS': 获取资源的可用选项
 * - 'POST': 创建资源
 * - 'PUT': 更新资源
 * - 'PATCH': 部分更新资源
 * - 'PURGE': 清除缓存
 * - 'LINK': 建立资源之间的关系
 * - 'UNLINK': 解除资源之间的关系
 *
 * 还可以是任意字符串，以支持自定义方法。
 */
export type IMethod = 'GET' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'POST' | 'PUT' | 'PATCH' | 'PURGE' | 'LINK' | 'UNLINK' | string;
